import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase/config";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error.message || fallback;
}

function waitForAuthenticatedUser(timeoutMs = 3000) {
  const auth = getAuth(app);

  if (auth.currentUser) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(
        new Error(
          "Bạn cần đăng nhập bằng Google/Firebase trước khi dùng Knowledge Base."
        )
      );
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        return;
      }

      clearTimeout(timeoutId);
      unsubscribe();
      resolve(user);
    });
  });
}

async function getFirebaseAuthHeaders() {
  const auth = getAuth(app);
  const user = auth.currentUser || (await waitForAuthenticatedUser());

  if (!user) {
    throw new Error(
      "Bạn cần đăng nhập bằng Google/Firebase trước khi dùng Knowledge Base."
    );
  }

  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function authenticatedRequest(config) {
  const headers = await getFirebaseAuthHeaders();
  return axios({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...headers,
    },
  });
}

export async function fetchFacebookCommentConfigs(userId) {
  const response = await axios.get(
    `${API_BASE_URL}/api/facebook-comments/configs`,
    {
      params: { userId },
    }
  );
  return response.data?.data || [];
}

export async function createFacebookCommentConfig(userId, config) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/configs`,
    {
      userId,
      config,
    }
  );
  return response.data?.data;
}

export async function updateFacebookCommentConfig(userId, configId, config) {
  const response = await axios.put(
    `${API_BASE_URL}/api/facebook-comments/configs/${configId}`,
    {
      userId,
      config,
    }
  );
  return response.data?.data;
}

export async function pauseFacebookCommentConfig(userId, configId) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/configs/${configId}/pause`,
    { userId }
  );
  return response.data?.data;
}

export async function resumeFacebookCommentConfig(userId, configId) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/configs/${configId}/resume`,
    { userId }
  );
  return response.data?.data;
}

export async function deleteFacebookCommentConfig(userId, configId) {
  const response = await axios.delete(
    `${API_BASE_URL}/api/facebook-comments/configs/${configId}`,
    {
      params: { userId },
    }
  );
  return response.data?.data;
}

export async function fetchFacebookCommentThreads(
  userId,
  pageId,
  status,
  pageIds = [],
  options = {}
) {
  const response = await axios.get(
    `${API_BASE_URL}/api/facebook-comments/threads`,
    {
      params: {
        userId,
        pageId: pageId || undefined,
        pageIds: Array.isArray(pageIds) && pageIds.length > 0
          ? pageIds.join(",")
          : undefined,
        status: status || undefined,
        limit: options.limit || undefined,
        view: options.view || undefined,
      },
    }
  );
  return response.data?.data || [];
}

export async function fetchLeadAnalytics(userId, days = 30, pageIds = []) {
  const response = await axios.get(
    `${API_BASE_URL}/api/facebook-comments/analytics`,
    {
      params: {
        userId,
        days,
        pageIds:
          Array.isArray(pageIds) && pageIds.length > 0
            ? pageIds.join(",")
            : undefined,
      },
    }
  );
  return response.data?.data || null;
}

export async function fetchFacebookCommentReviews(userId, configId) {
  const response = await axios.get(
    `${API_BASE_URL}/api/facebook-comments/reviews`,
    {
      params: { userId, configId },
    }
  );
  return response.data?.data || [];
}

export async function approveFacebookCommentReview(
  userId,
  configId,
  reviewId,
  replyText
) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/reviews/${reviewId}/approve`,
    {
      userId,
      configId,
      replyText,
    }
  );
  return response.data?.data;
}

export async function rejectFacebookCommentReview(
  userId,
  configId,
  reviewId,
  reason
) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/reviews/${reviewId}/reject`,
    {
      userId,
      configId,
      reason,
    }
  );
  return response.data?.data;
}

export async function retryFacebookCommentThread(userId, pageId, threadKey) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/threads/${threadKey}/retry`,
    {
      userId,
      pageId,
    }
  );
  return response.data?.data;
}

export async function connectTelegramGroup(userId, configId, chatId) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/telegram/connect`,
    {
      userId,
      configId,
      chatId,
    }
  );
  return response.data?.data;
}

export async function disconnectTelegramGroup(userId, configId) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/telegram/disconnect`,
    {
      userId,
      configId,
    }
  );
  return response.data?.data;
}

export async function testTelegramAlert(userId, configId) {
  const response = await axios.post(
    `${API_BASE_URL}/api/facebook-comments/telegram/test`,
    {
      userId,
      configId,
    }
  );
  return response.data?.data;
}

export async function fetchCommentQuota(userId) {
  const response = await axios.get(
    `${API_BASE_URL}/api/facebook-comments/quota`,
    {
      params: { userId },
    }
  );
  return response.data?.data || null;
}

export async function extractKnowledgeFromFile(payload) {
  const response = await authenticatedRequest({
    method: "post",
    url: `${API_BASE_URL}/api/knowledge/extract-file`,
    data: payload,
  });

  return response.data?.data;
}

export async function extractKnowledgeFromUrl(url) {
  const response = await authenticatedRequest({
    method: "post",
    url: `${API_BASE_URL}/api/knowledge/extract-url`,
    data: { url },
  });

  return response.data?.data;
}

export { getErrorMessage };
